import { AssistantMessageProps, AssistantMessage as DefaultAssistantMessage } from '@copilotkit/react-ui';
import * as React from 'react';
import { Citation, CitationsDisplay } from './CitationsDisplay';

const LEARN_MICROSOFT_PREFIX = 'https://learn.microsoft.com/';
const LEARN_MARKDOWN_LINK_REGEX = /\[([^\]]+)\]\((https:\/\/learn\.microsoft\.com\/[^\s)]+)\)/g;
const TRAILING_MARKDOWN_LINK_FRAGMENT_REGEX = /(?:\s+\[[^\]\n]*(?:\](?:\([^\n)]*)?)?)+\s*$/;

const getLearnMicrosoftCitations = (content: string): Citation[] => {
  const citations: Citation[] = [];
  const linkRegex = new RegExp(LEARN_MARKDOWN_LINK_REGEX);
  let match = linkRegex.exec(content);

  while (match) {
    const sourceName = match[1].replace(/\s+/g, ' ').trim();
    const sourceUrl = match[2];

    citations.push({
      sourceName: sourceName || sourceUrl,
      sourceUrl
    });

    match = linkRegex.exec(content);
  }

  return citations;
};

const getDisplayContentWithoutCitations = (content: string) =>
  content
    .replace(LEARN_MARKDOWN_LINK_REGEX, '')
    .replace(TRAILING_MARKDOWN_LINK_FRAGMENT_REGEX, '')
    .replace(/[ \t]+\n/g, '\n')
    .trimEnd();

type CitationsContextValue = {
  canCollectCitations: boolean;
  citations: Citation[];
  setCitations: React.Dispatch<React.SetStateAction<Citation[]>>;
  updateCitations: (citation: Citation) => void;
};

export const CitationsContext = React.createContext<CitationsContextValue | null>(null);

type CitationsContextProviderProps = React.PropsWithChildren<{
  canCollectCitations: boolean;
  sourceContent?: string;
}>;

const CitationsContextProvider: React.FC<CitationsContextProviderProps> = ({
  canCollectCitations,
  children,
  sourceContent = ''
}) => {
  const [citations, setCitations] = React.useState<Citation[]>([]);

  const updateCitations = React.useCallback((citation: Citation) => {
    if (!citation.sourceUrl.startsWith(LEARN_MICROSOFT_PREFIX)) {
      return;
    }

    setCitations((prevCitations) => {
      if (prevCitations.some((item) => item.sourceUrl === citation.sourceUrl)) {
        return prevCitations;
      }

      return [...prevCitations, citation];
    });
  }, []);

  React.useEffect(() => {
    if (!canCollectCitations || !sourceContent) {
      return;
    }

    getLearnMicrosoftCitations(sourceContent).forEach(updateCitations);
  }, [canCollectCitations, sourceContent, updateCitations]);

  const contextValue = React.useMemo(
    () => ({ canCollectCitations, citations, setCitations, updateCitations }),
    [canCollectCitations, citations, updateCitations]
  );

  return <CitationsContext.Provider value={contextValue}>{children}</CitationsContext.Provider>;
};

type ReferenceRendererProps = {
  children?: React.ReactNode;
  className?: string;
  href?: string;
  node?: unknown;
  rel?: string;
  sourceUrl?: string;
  sourceName?: string;
  target?: string;
  title?: string;
};

const CitationReference: React.FC<ReferenceRendererProps> = ({
  children,
  className,
  href,
  rel,
  sourceUrl,
  sourceName,
  target,
  title
}) => {
  const citationsContext = React.useContext(CitationsContext);
  const canCollectCitations = citationsContext?.canCollectCitations;
  const updateCitations = citationsContext?.updateCitations;
  const citationUrl = sourceUrl ?? href;

  React.useEffect(() => {
    if (!canCollectCitations || !updateCitations || !citationUrl?.startsWith(LEARN_MICROSOFT_PREFIX)) {
      return;
    }

    updateCitations({
      sourceName: sourceName ?? (typeof children === 'string' ? children : title ?? citationUrl),
      sourceUrl: citationUrl
    });
  }, [canCollectCitations, children, citationUrl, sourceName, title, updateCitations]);

  if (citationUrl?.startsWith(LEARN_MICROSOFT_PREFIX)) {
    return null;
  }

  if (!citationUrl) {
    return <>{children}</>;
  }

  const safeRel = target === '_blank' ? 'noopener noreferrer' : rel;

  return (
    <a href={citationUrl} className={className} rel={safeRel} target={target} title={title}>
      {children}
    </a>
  );
};

export const AssistantMessageWithCitations: React.FC<AssistantMessageProps> = (props) => {
  // Hide feedback buttons on the initial/welcome message since it's static content.
  const isInitialMessage = props.message?.id === props.message?.content;
  const assistantProps = isInitialMessage ? { ...props, onThumbsUp: undefined, onThumbsDown: undefined } : props;
  const messageKey = props.message?.id ?? props.message?.content ?? 'assistant-message';
  const canCollectCitations = !props.isGenerating && !props.isLoading;
  const rawContent = props.message?.content ?? '';
  const displayContent = React.useMemo(() => getDisplayContentWithoutCitations(rawContent), [rawContent]);
  const displayMessage = React.useMemo(
    () => (props.message ? { ...props.message, content: displayContent } : props.message),
    [displayContent, props.message]
  );
  const assistantMessageProps = React.useMemo(
    () => ({ ...assistantProps, message: displayMessage }),
    [assistantProps, displayMessage]
  );

  return (
    <CitationsContextProvider key={messageKey} canCollectCitations={canCollectCitations} sourceContent={rawContent}>
      <DefaultAssistantMessage {...assistantMessageProps} />
      <CitationsDisplay />
    </CitationsContextProvider>
  );
};

export const citationTagRenderers = {
  a: CitationReference,
  reference: CitationReference
};
